import { PrefillType } from '@weave/schema-gen-ts/dist/schemas/payments-platform/care-credit-integration/care_credit_integration.pb';
import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { CareCreditIntegrationCustom, CareCreditIntegrationV1 } from '@frontend/api-care-credit-integration';
import { InvoiceModel } from '@frontend/api-invoices';

dayjs.extend(utc);

export type UseCareCreditPurlQueryProps = {
  locationId: string | undefined;
  prefillType?: PrefillType;
  invoice: InvoiceModel | undefined;
  person: Person | undefined;
};
export type UseCareCreditPurlQueryOptions = {
  enabled?: boolean;
};
export const useCareCreditPurlQuery = (
  { locationId, invoice, prefillType = PrefillType.PREFILL_TYPE_UNSPECIFIED, person }: UseCareCreditPurlQueryProps,
  { enabled }: UseCareCreditPurlQueryOptions = {}
) => {
  const { firstName, lastName, email, contactNumber, dateOfBirth, addressLine1, city, state, zipCode } =
    CareCreditIntegrationCustom.getPatientQueryFields(person) || {};

  const {
    data: purl,
    isLoading: isPurlLoading,
    error: purlError,
  } = CareCreditIntegrationV1.Queries.useGeneratePatientDataPurlQuery({
    request: {
      locationId,
      paymentRequestId: invoice?.id,
      personId: person?.personId,
      prefillType,
      applicants: [
        {
          firstName,
          lastName,
          dateOfBirth,
          estimatedFee: invoice?.billedAmount,
          address: {
            addressLine1,
            city,
            state,
            zipCode,
          },
          contactInfo: {
            emailAddress: email,
            homePhone: contactNumber,
          },
        },
      ],
      transactionInfo: {
        purchaseAmount: invoice?.billedAmount,
        procedureDate: invoice?.billedAt ? dayjs(invoice.billedAt).local().format('YYYY-MM-DD') : undefined,
        // https://developer.syf.com/api-reference/patient-data-service-v1#operation/prefillPatientData
        // In CareCredit's TTP portal, there is a field called Invoice Number that is required. This is
        // distinctly different from the invoiceNumber field that gets mentioned in the docs link above.
        // I don't see any reason to pass a uuid into a customer-facing portal, when we can handle it
        // internally.
        //
        // The CC TTP portal suggests the following guidance for this field:
        // > For security, do not enter private information such as SSN or credit card numbers.
        // > If no invoice number, type "none".
        //
        practiceMemo: 'none',
      },
    },
    options: {
      enabled: !!locationId && !!person && enabled !== false,
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  });

  return { purl, isPurlLoading, purlError };
};
